import { IPromise, IQService, IWindowService } from "angular";
import { Transition } from "angular-ui-router";

/**
 * Coordinates hard reload for pages where a full page navigation is necessary.
 */
export class HardReloadService {
    private isReady: boolean;
    private deferredReloadRequested: boolean;
    private currentNavigation: IPromise<void>;

    constructor(private $window: IWindowService, private $q: IQService) {
        'ngInject';
        // Current navigation will be used to chain together reloads.
        this.currentNavigation = this.$q.resolve();
    }

    /**
     * Signal that the service should start reloading upon getting a request. Process
     * any deferred reload request.
     */
    init() {
        this.isReady = true;
        if (this.deferredReloadRequested) {
            this.reload();
        }
    }

    /**
     * Navigate to the given URL unless a navigation is currently in progress.
     * @param url The new URL to navigate to.
     */
    reload() {
        if (!this.isReady) {
            this.deferredReloadRequested = true;
            return;
        }
        this.currentNavigation = this.currentNavigation.then(() => {
            this.$window.location.reload();
            // Never resolve. This ensures that subsequent calls don't overwrite the process.
            return this.$q((resolve, reject) => {});
        });
    }

    /**
     * Return whether whether the transition would need to be hard reloaded in order to display it.
     * @param transition The transition to be evaluated.
     */
    shouldReload(transition: Transition): boolean {
        // Some pages perform pushState/replaceState on their own, which will be indicated by
        // having a requireReload param specified. Skip if moving from the initial state (name === "")
        if (transition.params().requireReload && transition.$from().name !== "") {
            if (transition.$from() !== transition.$to()) {
                // Always reload if the states are different.
                return true
            }
            const name = transition.$to().name;
            if (name.endsWith("**")) {
                // State change involves routing to a wildcard route that requires reload. This should always reload.
                return true
            }

            const { from: fromChanges, to: toChanges } = transition.treeChanges();
            const { id: fromID } = Array.from(fromChanges).find(p => p.state.name === name).paramValues;
            const { id: toID } = Array.from(toChanges).find(p => p.state.name === name).paramValues;
            if (fromID && !toID) {
                // Routing back to a null id column always either leads back to an index or a default page,
                // so perform a reload.
                return true;
            }
        }
        return false;
    }
}
